import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isViewer } from "@/lib/guards";
import { friendlyError } from "@/lib/errors";
import AppHeader from "@/components/AppHeader";
import { EmptyState, ErrorState, CardGridSkeleton } from "@/components/shared/StateViews";
import AddToCalendarSheet from "@/components/calendar/AddToCalendarSheet";
import StatusChip from "@/components/calendar/StatusChip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  todayISO, groupByDay, nextStatus, monthMatrix, weekDays, shiftISO,
  isSameMonthISO, isToday, fmtWeekdayShort, fmtDayNum, fmtMonthYear,
  fmtFullDate, fmtWeekRange, fmtTime, statusMeta,
} from "@/lib/calendar";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, BookOpen, Trash2, Clock,
  Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const VIEWS = [
  { key: "day", label: "Dia" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mês" },
];
const VIEW_KEYS = VIEWS.map((v) => v.key);
const WEEKDAY_LABELS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function rangeFor(view, cursor) {
  if (view === "day") return { from: cursor, to: cursor };
  if (view === "week") {
    const d = weekDays(cursor);
    return { from: d[0], to: d[6] };
  }
  const flat = monthMatrix(cursor).flat();
  return { from: flat[0], to: flat[flat.length - 1] };
}

// Content calendar (Epic 11.2): Day / Week / Month views over `/api/calendar`.
// Degrades to an "em breve" empty state while the backend (11.1) is still
// shipping (404), and is fully read-only for shared-workspace viewers.
export default function Calendar() {
  const { user } = useAuth();
  const viewer = isViewer(user);
  const [searchParams] = useSearchParams();
  const [today] = useState(() => todayISO());
  // Optional deep-link (?view=day&date=YYYY-MM-DD) — used by Minha Semana's
  // "Ver dia" jump. Read once on mount; invalid values fall back to today/day.
  const [view, setView] = useState(() => {
    const v = searchParams.get("view");
    return VIEW_KEYS.includes(v) ? v : "day";
  });
  const [cursor, setCursor] = useState(() => {
    const d = searchParams.get("date");
    return d && ISO_DATE.test(d) ? d : todayISO();
  });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notReady, setNotReady] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState(cursor);
  const [removing, setRemoving] = useState(null); // entry pending delete confirmation
  const [generatingId, setGeneratingId] = useState(null); // entry being sent to its agent
  const nav = useNavigate();
  const avatarsRef = useRef(null); // complete avatars, fetched once per visit

  const load = async () => {
    setLoading(true);
    setError(null);
    setNotReady(false);
    const { from, to } = rangeFor(view, cursor);
    try {
      const { data } = await api.get("/calendar", { params: { from, to } });
      setEntries(data || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotReady(true);
        setEntries([]);
      } else {
        setError(friendlyError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [view, cursor]);

  const grouped = useMemo(() => groupByDay(entries), [entries]);

  const unit = view === "day" ? "day" : view === "week" ? "week" : "month";
  const go = (n) => setCursor((c) => shiftISO(c, n, unit));

  const openAdd = (date) => {
    setAddDate(date || cursor);
    setAddOpen(true);
  };

  const cycleStatus = async (entry) => {
    if (viewer) return;
    const status = nextStatus(entry.status);
    setEntries((list) => list.map((e) => (e.id === entry.id ? { ...e, status } : e)));
    try {
      await api.patch(`/calendar/${entry.id}`, { status });
    } catch (err) {
      setEntries((list) => list.map((e) => (e.id === entry.id ? { ...e, status: entry.status } : e)));
      toast.error(friendlyError(err));
    }
  };

  // Click-to-generate (Epic 15.4): a planned entry with an agent_slug but no
  // artifact spawns a thread with the first complete avatar and drops the user
  // in the agent with the theme pre-sent (?autosend) + link-back (?entry).
  const generateForEntry = async (e) => {
    if (viewer || generatingId) return;
    setGeneratingId(e.id);
    try {
      if (!avatarsRef.current) {
        const { data } = await api.get("/avatars", { params: { status: "complete" } });
        avatarsRef.current = data || [];
      }
      const avatar = avatarsRef.current[0];
      if (!avatar) {
        toast.error("Crie um avatar primeiro para gerar este conteúdo.");
        return;
      }
      // Passa o id da thread recém-criada (?thread=) em vez de deixar o AgentChat
      // adivinhar pela "mais recente" — mesmo padrão de GuidedCalendarSheet.
      const { data: thread } = await api.post("/threads", {
        agent_slug: e.agent_slug,
        avatar_id: avatar.id,
      });
      const prompt = `Crie o conteúdo planejado para ${e.entry_date}: "${e.title}". Escolha você o tipo/formato mais adequado ao tema e gere direto, sem me fazer perguntas.`;
      nav(
        `/agent/${e.agent_slug}?thread=${thread.id}&autosend=${encodeURIComponent(prompt)}&entry=${e.id}`
      );
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Este agente faz parte de um plano que ainda não está liberado na sua conta.");
      } else {
        toast.error(friendlyError(err));
      }
    } finally {
      setGeneratingId(null);
    }
  };

  const performRemove = async () => {
    const entry = removing;
    setRemoving(null);
    if (!entry) return;
    const prev = entries;
    setEntries((list) => list.filter((e) => e.id !== entry.id));
    try {
      await api.delete(`/calendar/${entry.id}`);
      toast.success("Removido do calendário.");
    } catch (err) {
      setEntries(prev);
      toast.error(friendlyError(err));
    }
  };

  const headerLabel =
    view === "day"
      ? fmtFullDate(cursor)
      : view === "week"
        ? fmtWeekRange(...(() => { const d = weekDays(cursor); return [d[0], d[6]]; })())
        : fmtMonthYear(cursor);

  return (
    <div className="min-h-screen bg-brand-bg starfield relative" data-testid="calendar-page">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 pb-24 md:pb-10">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-ui mb-2">
              Calendário de Conteúdo
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-brand-ink">Sua agenda</h1>
          </div>
          {!viewer && !notReady && (
            <button
              data-testid="calendar-add"
              onClick={() => openAdd(cursor)}
              aria-label="Adicionar ao calendário"
              className="shrink-0 inline-flex items-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-4 sm:px-5 py-2.5 rounded-full text-[13px] font-ui transition-colors min-h-[44px]"
            >
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Adicionar</span>
            </button>
          )}
        </div>

        {/* View pills + navigation */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="inline-flex gap-1.5 bg-brand-surface border border-brand-line rounded-full p-1" role="tablist" aria-label="Visualização">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                role="tab"
                aria-selected={view === v.key}
                data-testid={`calendar-view-${v.key}`}
                onClick={() => setView(v.key)}
                className={`px-4 py-2 rounded-full text-[12.5px] font-ui transition-colors min-h-[40px] ${
                  view === v.key ? "bg-brand-rose text-white" : "text-brand-ink2 hover:text-brand-ink"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1">
            <button
              data-testid="calendar-prev"
              onClick={() => go(-1)}
              aria-label="Anterior"
              className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-surface border border-brand-line text-brand-ink2 hover:text-brand-rose hover:border-brand-rose transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="calendar-today"
              onClick={() => setCursor(today)}
              className="px-4 h-11 rounded-full bg-brand-surface border border-brand-line text-[12.5px] font-ui text-brand-ink2 hover:text-brand-rose hover:border-brand-rose transition-colors"
            >
              Hoje
            </button>
            <button
              data-testid="calendar-next"
              onClick={() => go(1)}
              aria-label="Próximo"
              className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-surface border border-brand-line text-brand-ink2 hover:text-brand-rose hover:border-brand-rose transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="mb-5 font-display text-2xl text-brand-ink" data-testid="calendar-range-label">
          {headerLabel}
        </div>

        {loading ? (
          <CardGridSkeleton count={3} />
        ) : notReady ? (
          <EmptyState
            testid="calendar-not-ready"
            icon={CalendarDays}
            title="Calendário em breve"
            description="Estamos preparando sua agenda de conteúdo. Volte logo — ela vai aparecer aqui."
          />
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="calendar-error" />
        ) : view === "month" ? (
          <MonthView
            cursor={cursor}
            today={today}
            grouped={grouped}
            onPickDay={(d) => { setCursor(d); setView("day"); }}
          />
        ) : view === "week" ? (
          <WeekView
            cursor={cursor}
            today={today}
            grouped={grouped}
            viewer={viewer}
            onCycle={cycleStatus}
            onAdd={openAdd}
            onOpenDay={(d) => { setCursor(d); setView("day"); }}
          />
        ) : (
          <DayView
            cursor={cursor}
            today={today}
            entries={grouped[cursor] || []}
            viewer={viewer}
            onCycle={cycleStatus}
            onRemove={setRemoving}
            onAdd={() => openAdd(cursor)}
            onGenerate={generateForEntry}
            generatingId={generatingId}
          />
        )}
      </div>

      <AddToCalendarSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        defaultDate={addDate}
        onAdded={load}
      />

      <AlertDialog open={Boolean(removing)} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent data-testid="calendar-remove-dialog" className="bg-brand-surface border-brand-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-brand-ink">
              Remover do calendário?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-brand-ink2">
              {removing?.title
                ? `"${removing.title}" sai da sua agenda. O conteúdo salvo na Biblioteca não é afetado.`
                : "A entrada sai da sua agenda. O conteúdo salvo na Biblioteca não é afetado."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-brand-elevated border-brand-line text-brand-ink2">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="calendar-remove-confirm"
              onClick={performRemove}
              className="bg-brand-rose hover:bg-brand-roseHover text-white"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Month ─────────────────────────────────────────────────────────────────────
function MonthView({ cursor, today, grouped, onPickDay }) {
  const weeks = monthMatrix(cursor);
  return (
    <div className="bg-brand-surface border border-brand-line rounded-3xl p-3 sm:p-4" data-testid="calendar-month">
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-1">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="text-center text-[10px] tracking-[0.14em] uppercase text-brand-ink3 font-ui py-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {weeks.flat().map((day) => {
          const dayEntries = grouped[day] || [];
          const inMonth = isSameMonthISO(day, cursor);
          const isCur = isToday(day, today);
          const dateLabel = fmtFullDate(day);
          const ariaLabel = dayEntries.length
            ? `${dateLabel}, ${dayEntries.length} ${dayEntries.length === 1 ? "conteúdo" : "conteúdos"}`
            : `${dateLabel}, sem conteúdos`;
          return (
            <button
              key={day}
              type="button"
              data-testid={`calendar-day-${day}`}
              onClick={() => onPickDay(day)}
              aria-label={ariaLabel}
              className={`min-h-[64px] sm:min-h-[92px] rounded-xl border p-1.5 text-left flex flex-col gap-1 transition-colors ${
                isCur ? "border-brand-rose bg-brand-rose/5" : "border-brand-line hover:border-brand-rose/50"
              } ${inMonth ? "" : "opacity-40"}`}
            >
              <span
                className={`text-[12px] font-ui leading-none ${
                  isCur ? "text-brand-rose font-semibold" : "text-brand-ink2"
                }`}
              >
                {fmtDayNum(day)}
              </span>
              <span className="flex flex-col gap-0.5 overflow-hidden">
                {dayEntries.slice(0, 2).map((e) => {
                  const meta = statusMeta(e, today);
                  return (
                    <span
                      key={e.id}
                      className={`truncate rounded px-1 py-0.5 text-[9.5px] leading-tight ${meta.className}`}
                    >
                      {e.title}
                    </span>
                  );
                })}
                {dayEntries.length > 2 && (
                  <span className="text-[9.5px] text-brand-ink3 font-ui px-1">+{dayEntries.length - 2}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Week ──────────────────────────────────────────────────────────────────────
function WeekView({ cursor, today, grouped, viewer, onCycle, onAdd, onOpenDay }) {
  const days = weekDays(cursor);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-7 gap-3" data-testid="calendar-week">
      {days.map((day) => {
        const dayEntries = grouped[day] || [];
        const isCur = isToday(day, today);
        return (
          <div
            key={day}
            className={`bg-brand-surface border rounded-2xl p-3 flex flex-col gap-2 ${
              isCur ? "border-brand-rose/60" : "border-brand-line"
            }`}
            data-testid={`calendar-week-day-${day}`}
          >
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => onOpenDay(day)}
                className="text-left"
              >
                <span className={`block text-[10px] tracking-[0.14em] uppercase font-ui ${isCur ? "text-brand-rose" : "text-brand-ink3"}`}>
                  {fmtWeekdayShort(day)}
                </span>
                <span className={`block font-display text-lg leading-none ${isCur ? "text-brand-rose" : "text-brand-ink"}`}>
                  {fmtDayNum(day)}
                </span>
              </button>
              {!viewer && (
                <button
                  type="button"
                  onClick={() => onAdd(day)}
                  aria-label={`Adicionar em ${fmtDayNum(day)}`}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-brand-ink3 hover:text-brand-rose hover:bg-brand-elevated transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              {dayEntries.length === 0 ? (
                <span className="text-[12px] text-brand-ink3 font-ui py-1">—</span>
              ) : (
                dayEntries.map((e) => (
                  <div key={e.id} className="rounded-xl bg-brand-elevated border border-brand-line px-2.5 py-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      {e.entry_time && (
                        <span className="text-[10.5px] text-brand-ink2 font-ui tabular-nums">{fmtTime(e.entry_time)}</span>
                      )}
                      <StatusChip
                        entry={e}
                        today={today}
                        onCycle={viewer ? undefined : () => onCycle(e)}
                        testid={`calendar-status-${e.id}`}
                      />
                    </div>
                    <div className="text-[13px] text-brand-ink leading-snug line-clamp-2">{e.title}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Day ───────────────────────────────────────────────────────────────────────
function DayView({ cursor, today, entries, viewer, onCycle, onRemove, onAdd, onGenerate, generatingId }) {
  return (
    <div data-testid="calendar-day">
      {entries.length === 0 ? (
        <EmptyState
          testid="calendar-day-empty"
          icon={CalendarDays}
          title="Nada agendado neste dia"
          description={viewer ? "Sem conteúdos planejados para esta data." : "Adicione um conteúdo salvo ou um lembrete manual."}
          actionLabel={viewer ? undefined : "Adicionar conteúdo"}
          onAction={viewer ? undefined : onAdd}
        />
      ) : (
        <div className="space-y-2.5">
          {entries.map((e) => (
            <div
              key={e.id}
              data-testid={`calendar-entry-${e.id}`}
              className="bg-brand-surface border border-brand-line rounded-2xl p-4 flex items-start gap-3"
            >
              <div className="w-14 shrink-0 pt-0.5 text-center">
                {e.entry_time ? (
                  <span className="font-ui text-[15px] text-brand-ink tabular-nums">{fmtTime(e.entry_time)}</span>
                ) : (
                  <Clock className="w-4 h-4 text-brand-ink3 mx-auto" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-[17px] text-brand-ink leading-tight mb-1">{e.title}</div>
                <div className="flex flex-wrap items-center gap-x-2">
                  <StatusChip
                    entry={e}
                    today={today}
                    onCycle={viewer ? undefined : () => onCycle(e)}
                    expandHit
                    testid={`calendar-status-${e.id}`}
                  />
                  {e.artifact && e.artifact.type !== "calendar" && (
                    <Link
                      to={e.artifact_id ? `/library?artifact=${e.artifact_id}` : "/library"}
                      className="inline-flex items-center gap-1 min-h-[44px] py-2 -my-1 text-[11.5px] font-ui text-brand-ink2 hover:text-brand-rose transition-colors"
                    >
                      <BookOpen className="w-3.5 h-3.5" /> Ver na biblioteca
                    </Link>
                  )}
                  {!viewer && e.status === "planned" && (!e.artifact || e.artifact.type === "calendar") && e.agent_slug && (
                    <button
                      type="button"
                      data-testid={`calendar-generate-${e.id}`}
                      onClick={() => onGenerate(e)}
                      disabled={Boolean(generatingId)}
                      className="inline-flex items-center gap-1 min-h-[44px] py-2 -my-1 text-[11.5px] font-ui text-brand-rose hover:text-brand-roseHover transition-colors disabled:opacity-50"
                    >
                      {generatingId === e.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Sparkles className="w-3.5 h-3.5" />}
                      Gerar conteúdo ✦
                    </button>
                  )}
                </div>
              </div>
              {!viewer && (
                <button
                  type="button"
                  data-testid={`calendar-remove-${e.id}`}
                  onClick={() => onRemove(e)}
                  aria-label="Remover"
                  className="shrink-0 w-11 h-11 flex items-center justify-center rounded-full text-brand-ink3 hover:text-brand-rose hover:bg-brand-elevated transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!viewer && entries.length > 0 && (
        <button
          type="button"
          data-testid="calendar-day-add"
          onClick={onAdd}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-dashed border-brand-line hover:border-brand-rose text-brand-ink2 hover:text-brand-rose rounded-2xl py-3.5 text-[13.5px] font-ui transition-colors min-h-[48px]"
        >
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      )}
    </div>
  );
}
